import { Box, Stack, Typography } from '@mui/material';

import Note from '@/designs-dev/annotations/Note';
import { type HandoverSpec } from '@/designs-dev/handover/types';
import {
  CALL,
  CARE_ASSESSMENT,
  calculatorCard,
  couldGet,
  options,
} from '@/karehero-app/handover/dynamic-flow-app';
import { homeTab } from '@/karehero-app/handover/dynamic-flow-slices';
import CareProfileScreen from '@/karehero-app/tell-us-once/intake/CareProfileScreen';
import { CARER_INITIALS } from '@/karehero-app/tell-us-once/intake/content';
import ChatOption from '@/karehero-app/tell-us-once/intake/ChatOption';
import Flow from '@/karehero-app/tell-us-once/intake/Flow';
import FromUsCard from '@/karehero-app/tell-us-once/intake/FromUsCard';

const noop = () => {};

// The Home concepts on the concepting page, as they are, so this page points at
// them rather than drawing them again.
const at = (initial: Parameters<typeof Flow>[0]['initial']) => () => (
  <Flow initial={initial} option={2} version='2.0' />
);

const toDesign = (
  <Note title='To design' variant='body2'>
    Not drawn yet.
  </Note>
);

/**
 * What Home shows someone straight after onboarding, three ways, for the same
 * person and the same answers, so the directions compare like for like: a chat
 * they lead, a Home that guesses for them, or one thing guessed with the chat
 * under it. Each has its first visit and a visit after it.
 *
 * Built from screens already in Loft where one fits; the source is in each
 * title so it can be swapped.
 */
export const homeFirstVisitSpec: HandoverSpec = {
  id: 'home-first-visit',
  title: 'Home, first visit',
  description: '',
  canvas: true,
  links: [
    {
      label: 'Dynamic flows, across the app',
      url: '/tell-us-once/flows/dynamic-flow-app',
    },
    {
      label: 'Dynamic flow concepting',
      url: '/tell-us-once/flows/dynamic-flow',
    },
  ],
  flows: [
    {
      title: 'Just a chat',
      arrows: false,
      numbered: false,
      steps: [
        {
          id: 'chat-first',
          title: 'First visit',
          render: () => homeTab(null),
          note: toDesign,
        },
        {
          id: 'chat-back',
          title: 'Coming back · Concept 1.0',
          render: at('home-conversations'),
        },
      ],
    },
    {
      title: 'Preempt',
      arrows: false,
      numbered: false,
      beside: true,
      note: (
        <Note title='Priorities' variant='body2'>
          <Stack spacing={1.5}>
            <Typography variant='body2'>
              Preempting only works if we have decided what comes first and why.
            </Typography>
            <Typography variant='body2'>
              One lead, with the primary button and the reason it is first. Two
              or three others, quieter and set apart. A way to say not that, so
              a wrong guess costs one tap.
            </Typography>
            <Typography variant='body2'>
              Here the calculator leads. They have no benefits yet, are 66 or
              over and need help day and night. That is money they are probably
              missing, it is quick to find out, and the claims come out of it.
            </Typography>
            <Typography variant='body2'>What decides the order:</Typography>
            <Box component='ul' sx={{ m: 0, pl: 2.5 }}>
              {[
                'What they asked for',
                'Urgency or risk',
                'What they are missing',
                'Effort to value',
                'What it unlocks',
              ].map((r) => (
                <Typography key={r} component='li' variant='body2'>
                  {r}
                </Typography>
              ))}
            </Box>
            <Typography variant='body2'>
              What they asked for beats what we infer. Urgency beats everything.
            </Typography>
            <Typography variant='body2'>
              Not decided yet: what leads when nothing stands out or two things
              tie, whether the reason always shows, and whether the lead changes
              once it is done.
            </Typography>
          </Stack>
        </Note>
      ),
      steps: [
        {
          id: 'preempt-first',
          title: 'First visit · Slice 4, the calculator then the rest',
          render: () =>
            homeTab(
              <Stack spacing={3}>
                {calculatorCard()}
                {options('Also for you', [CARE_ASSESSMENT, CALL])}
              </Stack>,
            ),
        },
        {
          id: 'preempt-back',
          title: 'Coming back · Slice 5, the result in its place',
          render: () =>
            homeTab(
              <Stack spacing={3}>
                {couldGet(noop)}
                {options('Next', ['Start the Attendance Allowance claim'])}
              </Stack>,
            ),
        },
      ],
    },
    {
      title: 'Both',
      arrows: false,
      numbered: false,
      beside: true,
      steps: [
        {
          id: 'both-back',
          title: 'Coming back · Concept 4, a button to ask',
          render: at('home-c4-ask'),
          alt: {
            title: 'The button in lime',
            render: at('home-c4-ask-lime'),
          },
        },
      ],
    },
    {
      // Preempting, with what they told us carried on Home: only the answers
      // that put the lead first, each one fixable there, and the way to all of
      // it. The full record lives in Profile.
      title: 'Preempt, with what they told us',
      arrows: false,
      numbered: false,
      beside: true,
      steps: [
        {
          id: 'told-first',
          title: 'First visit · the lead, and the answers behind it',
          render: () =>
            homeTab(
              <Stack spacing={3}>
                {calculatorCard()}
                <FromUsCard
                  label={`What you've told us`}
                  items={[
                    'No benefits yet',
                    '66 or over',
                    'Needs help day and night',
                  ]}
                  sentence
                  onEdit={noop}
                >
                  <ChatOption
                    label='See everything you told us'
                    onClick={noop}
                  />
                </FromUsCard>
                {options('Also for you', [CARE_ASSESSMENT, CALL])}
              </Stack>,
            ),
        },
        {
          id: 'told-all',
          // The master Profile flows page's screen, on About: what we hold,
          // which is everything they told us. Assess beside it, where those
          // answers go to work.
          title: 'Everything they told us · Master Profile, About',
          render: () => (
            <CareProfileScreen
              initialTab='profile'
              account={{ initials: CARER_INITIALS }}
            />
          ),
          alt: {
            title: 'Master Profile, Assess',
            render: () => (
              <CareProfileScreen
                initialTab='claims'
                account={{ initials: CARER_INITIALS }}
              />
            ),
          },
        },
      ],
    },
  ],
  states: [],
  components: {},
};
